import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ICompany,
  ICompanyJobStructure,
  ICompanyMember,
} from '../common/models/Company'
import { FilterChipDescriptor } from '../components/filter/FilterChips'
import type { FilterConfig, FilterOption } from '../components/filter/Filter'
import { IconType } from '../components/icon/IconType'
import { NO_DOMAIN_KEY } from '../utils/companyFilterMatchers'

export type CompanyFilterKey = 'team' | 'domain' | 'job' | 'culturalPhase'

// What the filters read from a company. Teams are optional so the public
// careers page, which only has the job structure, can use the same filters.
export type IFilterableCompany = ICompanyJobStructure &
  Partial<Pick<ICompany, 'teams'>>

export interface ICompanyFilterDescriptor {
  key: CompanyFilterKey
  icon: IconType
  label: string
  config: FilterConfig
  options: Array<FilterOption<string>>
  value: string[]
  onChange: (next: string[]) => void
}

export interface CompanyFilterValues {
  team: string[]
  domain: string[]
  job: string[]
  culturalPhase: string[]
}

export interface ICompanyFilterScope {
  members: ICompanyMember[]
  teamIds: string[]
}

export interface IUseCompanyFiltersOptions<T> {
  team?: boolean
  domain?: boolean
  job?: boolean
  culturalPhase?: boolean
  initial?: Partial<CompanyFilterValues>
  scope?: ICompanyFilterScope
  matches: (
    entity: T,
    values: CompanyFilterValues,
    company: IFilterableCompany
  ) => boolean
}

export interface IUseCompanyFiltersResult<T> {
  descriptors: ICompanyFilterDescriptor[]
  chips: FilterChipDescriptor[]
  matches: (entity: T) => boolean
  hasActiveFilters: boolean
  clearAll: () => void
  values: CompanyFilterValues
  setters: {
    setTeamFilter: React.Dispatch<React.SetStateAction<string[]>>
    setDomainFilter: React.Dispatch<React.SetStateAction<string[]>>
    setJobFilter: React.Dispatch<React.SetStateAction<string[]>>
    setCulturalPhaseFilter: React.Dispatch<React.SetStateAction<string[]>>
  }
}

const buildTeamOptions = (
  company: IFilterableCompany,
  allowedTeamIds?: Set<string>
): Array<FilterOption<string>> =>
  (company.teams ?? [])
    .filter((team) => !allowedTeamIds || allowedTeamIds.has(team._id))
    .map((team) => ({
      value: team._id,
      label: team.name,
    }))

// Ordered by `rank`, so the filter follows the level order the admin set on the
// culture (DNA) settings page rather than the order the ranks happen to be stored in.
const buildCulturalPhaseOptions = (
  company: ICompanyJobStructure,
  allowedRankIds?: Set<string>
): Array<FilterOption<string>> =>
  [...company.cultureRanks]
    .sort((a, b) => a.rank - b.rank)
    .filter((rank) => !allowedRankIds || allowedRankIds.has(rank._id))
    .map((rank) => ({
      value: rank._id,
      label: rank.title,
    }))

/**
 * The domains, plus a catch-all for jobs without one — but only when there is a
 * real domain to contrast it with, the same rule the diagram and its hover card
 * follow.
 */
const buildDomainOptions = (
  company: ICompanyJobStructure,
  otherLabel: string
): Array<FilterOption<string>> => {
  const domains = (company.departments || [])
    .filter((department) =>
      company.jobs.some(
        (job) => String(job.department?._id) === String(department._id)
      )
    )
    .map((department) => ({
      value: String(department._id),
      label: department.name,
    }))
  if (domains.length === 0) return []
  return company.jobs.some((job) => !job.department)
    ? [...domains, { value: NO_DOMAIN_KEY, label: otherLabel }]
    : domains
}

export const buildJobOptions = (
  company: ICompanyJobStructure,
  otherLabel: string,
  jobScope?: { jobIds: Set<string>; levelIds: Set<string> }
): Array<FilterOption<string>> => {
  const scopedJobs = jobScope
    ? company.jobs.filter((job) => jobScope.jobIds.has(job._id))
    : company.jobs

  const jobsByDepartment = new Map<
    string,
    { name: string; jobs: ICompanyJobStructure['jobs'] }
  >()
  scopedJobs.forEach((job) => {
    const key = job.department?._id || NO_DOMAIN_KEY
    const name = job.department?.name || otherLabel
    const entry = jobsByDepartment.get(key)
    if (entry) {
      entry.jobs.push(job)
    } else {
      jobsByDepartment.set(key, { name, jobs: [job] })
    }
  })

  const groups = Array.from(jobsByDepartment.entries())
    .sort(([keyA, a], [keyB, b]) => {
      if (keyA === NO_DOMAIN_KEY) return 1
      if (keyB === NO_DOMAIN_KEY) return -1
      return a.name.localeCompare(b.name)
    })
    .map(([deptKey, { name, jobs }]) => ({
      value: `dept-${deptKey}`,
      label: name,
      selectable: false,
      children: jobs
        .map((job) => {
          const scopedLevels = jobScope
            ? job.levels.filter((level) => jobScope.levelIds.has(level._id))
            : job.levels
          if (scopedLevels.length === 0) return null
          const isSingleLevel = scopedLevels.length === 1
          return {
            value: isSingleLevel ? scopedLevels[0]._id : job._id,
            label: isSingleLevel ? scopedLevels[0].title : job.title,
            children: isSingleLevel
              ? undefined
              : scopedLevels.map((level) => ({
                  value: level._id,
                  label: level.title,
                })),
          }
        })
        .filter((c): c is NonNullable<typeof c> => c !== null),
    }))
    .filter((dept) => dept.children.length > 0)

  // One catch-all group is not a grouping: without departments there is nothing
  // to tell "other jobs" apart from, so the jobs stand on their own.
  return groups.length === 1 && groups[0].value === `dept-${NO_DOMAIN_KEY}`
    ? groups[0].children
    : groups
}

const buildSimpleChips = (
  filter: string[],
  setFilter: React.Dispatch<React.SetStateAction<string[]>>,
  keyPrefix: string,
  lookupLabel: (value: string) => string | undefined
): FilterChipDescriptor[] =>
  filter
    .map<FilterChipDescriptor | null>((value) => {
      const label = lookupLabel(value)
      if (label === undefined) return null
      return {
        key: `${keyPrefix}-${value}`,
        label,
        onRemove: () => setFilter((prev) => prev.filter((v) => v !== value)),
      }
    })
    .filter((c): c is FilterChipDescriptor => c !== null)

const buildJobChips = (
  jobFilter: string[],
  setJobFilter: React.Dispatch<React.SetStateAction<string[]>>,
  company: ICompanyJobStructure
): FilterChipDescriptor[] => {
  const chips: FilterChipDescriptor[] = []
  const handled = new Set<string>()

  company.jobs.forEach((job) => {
    if (job.levels.length <= 1) return
    const levelIds = job.levels.map((l) => l._id)
    const allSelected = levelIds.every((id) => jobFilter.includes(id))
    if (allSelected) {
      chips.push({
        key: `job-${job._id}`,
        label: job.title,
        onRemove: () =>
          setJobFilter((prev) => prev.filter((v) => !levelIds.includes(v))),
      })
      levelIds.forEach((id) => handled.add(id))
    }
  })

  jobFilter.forEach((value) => {
    if (handled.has(value)) return

    const singleLevelJob = company.jobs.find(
      (j) => j.levels.length === 1 && j._id === value
    )
    if (singleLevelJob) {
      chips.push({
        key: `job-${value}`,
        label: singleLevelJob.title,
        onRemove: () => setJobFilter((prev) => prev.filter((v) => v !== value)),
      })
      return
    }

    for (const job of company.jobs) {
      const level = job.levels.find((l) => l._id === value)
      if (level) {
        chips.push({
          key: `level-${value}`,
          label: level.title,
          onRemove: () =>
            setJobFilter((prev) => prev.filter((v) => v !== value)),
        })
        break
      }
    }
  })

  return chips
}

export const useCompanyFilters = <T>(
  company: IFilterableCompany | undefined,
  options: IUseCompanyFiltersOptions<T>
): IUseCompanyFiltersResult<T> => {
  const { t } = useTranslation()
  const enableTeam = options.team === true
  const enableDomain = options.domain === true
  const enableJob = options.job === true
  const enableCulturalPhase = options.culturalPhase === true
  const { matches: matchEntity } = options

  const [teamFilter, setTeamFilter] = useState<string[]>(
    options.initial?.team ?? []
  )
  const [domainFilter, setDomainFilter] = useState<string[]>(
    options.initial?.domain ?? []
  )
  const [jobFilter, setJobFilter] = useState<string[]>(
    options.initial?.job ?? []
  )
  const [culturalPhaseFilter, setCulturalPhaseFilter] = useState<string[]>(
    options.initial?.culturalPhase ?? []
  )

  const scope = options.scope
  const allowedTeamIds = useMemo(
    () => (scope ? new Set(scope.teamIds) : undefined),
    [scope]
  )
  const allowedRankIds = useMemo(
    () =>
      scope
        ? new Set(
            scope.members
              .map((m) => m.jobLevel?.cultureRankId)
              .filter((id): id is string => !!id)
          )
        : undefined,
    [scope]
  )
  const jobScope = useMemo(() => {
    if (!scope) return undefined
    const jobIds = new Set<string>()
    const levelIds = new Set<string>()
    scope.members.forEach((m) => {
      if (m.job?.jobId) jobIds.add(m.job.jobId)
      if (m.job?.jobLevelId) levelIds.add(m.job.jobLevelId)
    })
    return { jobIds, levelIds }
  }, [scope])

  const teamOptions = useMemo(
    () => (company ? buildTeamOptions(company, allowedTeamIds) : []),
    [company, allowedTeamIds]
  )
  const domainOptions = useMemo(
    () =>
      company ? buildDomainOptions(company, t('addEditUser.otherJobs')) : [],
    [company, t]
  )
  const jobOptions = useMemo(
    () =>
      company
        ? buildJobOptions(company, t('addEditUser.otherJobs'), jobScope)
        : [],
    [company, t, jobScope]
  )
  const culturalPhaseOptions = useMemo(
    () => (company ? buildCulturalPhaseOptions(company, allowedRankIds) : []),
    [company, allowedRankIds]
  )

  const descriptors = useMemo<ICompanyFilterDescriptor[]>(() => {
    const list: ICompanyFilterDescriptor[] = []
    if (enableTeam) {
      list.push({
        key: 'team',
        icon: 'Team',
        label: t('general.team', { count: 1 }),
        config: { mode: 'multi', searchable: true },
        options: teamOptions,
        value: teamFilter,
        onChange: setTeamFilter,
      })
    }
    // Before the job filter: a domain is the coarser cut of the same axis, so it
    // reads as narrowing down rather than as a second, unrelated question.
    if (enableDomain && domainOptions.length > 0) {
      list.push({
        key: 'domain',
        icon: 'Department',
        label: t('general.domain'),
        config: { mode: 'multi', searchable: true },
        options: domainOptions,
        value: domainFilter,
        onChange: setDomainFilter,
      })
    }
    if (enableJob) {
      list.push({
        key: 'job',
        icon: 'Job',
        label: t('general.job', { count: 1 }),
        config: {
          mode: 'multi',
          layered: true,
          grouped: true,
          searchable: true,
          defaultCollapsed: true,
        },
        options: jobOptions,
        value: jobFilter,
        onChange: setJobFilter,
      })
    }
    if (enableCulturalPhase) {
      list.push({
        key: 'culturalPhase',
        icon: 'Culture',
        label: t('general.culturalPhase'),
        config: { mode: 'multi' },
        options: culturalPhaseOptions,
        value: culturalPhaseFilter,
        onChange: setCulturalPhaseFilter,
      })
    }
    return list
  }, [
    enableTeam,
    enableDomain,
    enableJob,
    enableCulturalPhase,
    teamOptions,
    domainOptions,
    jobOptions,
    culturalPhaseOptions,
    teamFilter,
    domainFilter,
    jobFilter,
    culturalPhaseFilter,
    t,
  ])

  const chips = useMemo<FilterChipDescriptor[]>(() => {
    if (!company) return []
    const list: FilterChipDescriptor[] = []
    if (enableTeam) {
      list.push(
        ...buildSimpleChips(
          teamFilter,
          setTeamFilter,
          'team',
          (value) => company.teams?.find((team) => team._id === value)?.name
        )
      )
    }
    if (enableDomain) {
      list.push(
        ...buildSimpleChips(domainFilter, setDomainFilter, 'domain', (value) =>
          value === NO_DOMAIN_KEY
            ? t('addEditUser.otherJobs')
            : company.departments?.find((d) => String(d._id) === value)?.name
        )
      )
    }
    if (enableJob) list.push(...buildJobChips(jobFilter, setJobFilter, company))
    if (enableCulturalPhase) {
      list.push(
        ...buildSimpleChips(
          culturalPhaseFilter,
          setCulturalPhaseFilter,
          'phase',
          (value) => company.cultureRanks.find((r) => r._id === value)?.title
        )
      )
    }
    return list
  }, [
    company,
    enableTeam,
    enableDomain,
    enableJob,
    enableCulturalPhase,
    teamFilter,
    domainFilter,
    jobFilter,
    culturalPhaseFilter,
    t,
  ])

  const hasActiveFilters =
    (enableTeam && teamFilter.length > 0) ||
    (enableDomain && domainFilter.length > 0) ||
    (enableJob && jobFilter.length > 0) ||
    (enableCulturalPhase && culturalPhaseFilter.length > 0)

  const clearAll = useCallback(() => {
    if (enableTeam) setTeamFilter([])
    if (enableDomain) setDomainFilter([])
    if (enableJob) setJobFilter([])
    if (enableCulturalPhase) setCulturalPhaseFilter([])
  }, [enableTeam, enableDomain, enableJob, enableCulturalPhase])

  const matches = useCallback(
    (entity: T): boolean => {
      if (!company) return true
      return matchEntity(
        entity,
        {
          team: enableTeam ? teamFilter : [],
          domain: enableDomain ? domainFilter : [],
          job: enableJob ? jobFilter : [],
          culturalPhase: enableCulturalPhase ? culturalPhaseFilter : [],
        },
        company
      )
    },
    [
      company,
      matchEntity,
      enableTeam,
      enableDomain,
      enableJob,
      enableCulturalPhase,
      teamFilter,
      domainFilter,
      jobFilter,
      culturalPhaseFilter,
    ]
  )

  return {
    descriptors,
    chips,
    matches,
    hasActiveFilters,
    clearAll,
    values: {
      team: teamFilter,
      domain: domainFilter,
      job: jobFilter,
      culturalPhase: culturalPhaseFilter,
    },
    setters: {
      setTeamFilter,
      setDomainFilter,
      setJobFilter,
      setCulturalPhaseFilter,
    },
  }
}
